/** VISTOERA_PLATFORM_ADMIN_CANVAS_NODE_CATALOG_ICON_ORDER_AVAILABILITY_
 * AND_VISIBILITY_FOUNDATION_V2 — tenant/session Node Catalog cache.
 *
 * Fetched ONCE per (userId, workspaceId) Canvas session (never per-node /
 * per-hover). Admin mutations take effect on next fetch / page refresh;
 * Backend always re-checks availability on create/run.
 *
 * VistoeraTASK-00583 — until the catalog is READY, creatable checks are
 * deny-by-default (never flash the full local registry as "all online").
 * Load failure is ERROR, not "everything enabled".
 *
 * Icon assignment: CanvasNodeCatalogEntry.icon_ref is canonical.
 * Legacy node-metadata/icon-overrides must NOT overwrite catalog refs.
 */
import type { CanvasNodeCatalogEntry } from '../../../api'
import { isNodeEffectiveAllowed } from './authMatrixRuntime'

export type NodeCatalogStatus = 'loading' | 'ready' | 'error'

let _entriesByType: Map<string, CanvasNodeCatalogEntry> = new Map()
let _orderedTypes: string[] = []
let _availabilityByType: Map<string, string> = new Map()
/** Cache key = `${userId}::${workspaceId}` — never reuse across accounts. */
let _fetchedForKey: string | null = null
let _status: NodeCatalogStatus = 'loading'
let _revision = 0
let _inflight: { key: string; promise: Promise<void>; token: number } | null = null
/** Monotonic request token (VistoeraTASK-00576): a response is applied only if
 * it is still the LATEST request - a slower fetch for a previous identity /
 * workspace can never overwrite the current one. */
let _requestSeq = 0
const _listeners = new Set<() => void>()

function _notify() {
  _revision += 1
  for (const l of _listeners) l()
}

function catalogCacheKey(workspaceId: string, userId: string | null | undefined): string {
  return `${userId || ''}::${workspaceId}`
}

export function getNodeCatalogRevision(): number {
  return _revision
}

export function getNodeCatalogStatus(): NodeCatalogStatus {
  return _status
}

export function subscribeNodeCatalog(listener: () => void): () => void {
  _listeners.add(listener)
  return () => {
    _listeners.delete(listener)
  }
}

export function isNodeCatalogLoaded(): boolean {
  return _status === 'ready' || _status === 'error'
}

export function getCachedNodeCatalogEntries(): CanvasNodeCatalogEntry[] {
  return _orderedTypes.map((t) => _entriesByType.get(t)!).filter(Boolean)
}

/** Exact tenant-creatable order from the last catalog fetch (API order). */
export function getCachedNodeCatalogOrder(): string[] {
  return [..._orderedTypes]
}

export function getCachedCatalogEntry(nodeType: string): CanvasNodeCatalogEntry | undefined {
  return _entriesByType.get(nodeType)
}

/**
 * True when catalog is READY and the node is creatable for tenants.
 * LOADING / ERROR → false (VistoeraTASK-00583: never default-open unconfirmed).
 * VistoeraTASK-00634 — also requires effective auth (deny while auth matrix
 * loading so the palette never flashes unauthorized nodes).
 */
export function isNodeCreatableInCatalog(nodeType: string): boolean {
  if (_status !== 'ready') return false
  const entry = _entriesByType.get(nodeType)
  if (!entry) return false
  const catalogOk =
    entry.availability_status === 'ONLINE' &&
    entry.is_enabled !== false &&
    entry.implementation_status === 'READY'
  if (!catalogOk) return false
  return isNodeEffectiveAllowed(nodeType)
}

export function isNodeOfflineInCatalog(nodeType: string): boolean {
  if (_status !== 'ready') return false
  const status = _availabilityByType.get(nodeType)
  if (status === 'OFFLINE') return true
  const entry = _entriesByType.get(nodeType)
  if (entry) return entry.availability_status === 'OFFLINE' || entry.is_enabled === false
  // Known type missing from creatable entries with no availability row: not offline.
  return false
}

export function applyTenantNodeCatalog(
  entries: CanvasNodeCatalogEntry[],
  availabilityByNodeType?: Record<string, string>,
): void {
  const next = new Map<string, CanvasNodeCatalogEntry>()
  const order: string[] = []
  for (const e of entries) {
    next.set(e.node_type, e)
    order.push(e.node_type)
  }
  _entriesByType = next
  _orderedTypes = order
  if (availabilityByNodeType) {
    _availabilityByType = new Map(Object.entries(availabilityByNodeType))
  }
  _notify()
}

async function _applyCatalogIconAssignments(
  entries: CanvasNodeCatalogEntry[],
  workspaceId: string,
): Promise<void> {
  const overrides: Record<string, string> = {}
  const customUrls: Record<string, string> = {}
  for (const e of entries) {
    if (!e.icon_ref) continue
    overrides[e.node_type] = e.icon_ref
    const url = (e as CanvasNodeCatalogEntry & { icon_url?: string | null }).icon_url
    if (e.icon_ref.startsWith('custom:') && url) {
      customUrls[e.icon_ref] = url
    } else if (e.icon_ref.startsWith('custom:')) {
      // Workspace-scoped content URL (stable, no admin gate).
      const id = e.icon_ref.slice('custom:'.length)
      customUrls[e.icon_ref] =
        `/api/saas/workspaces/${encodeURIComponent(workspaceId)}/node-catalog/custom-icons/${encodeURIComponent(id)}/content`
    }
  }
  // Lazy: icon assignment pulls React JSX icon tables — keep catalog cache
  // importable from node tests without a React harness.
  const { mergeCustomIconUrlMap, setCanvasIconAssignments } = await import('./canvasNodeIconAssignment')
  setCanvasIconAssignments(overrides)
  if (Object.keys(customUrls).length) mergeCustomIconUrlMap(customUrls)
  // Keep legacy override map in sync for getNodeDefinition / older callers.
  const { setNodeIconOverrides } = await import('./nodeRegistry')
  setNodeIconOverrides(overrides)
}

function _clearEntries(): void {
  _entriesByType = new Map()
  _orderedTypes = []
  _availabilityByType = new Map()
}

/**
 * Drop the in-memory catalog so the next Canvas boot cannot reuse another
 * identity's availability (account switch / logout).
 */
export function invalidateNodeCatalogCache(): void {
  _requestSeq += 1
  _inflight = null
  _fetchedForKey = null
  _status = 'loading'
  _clearEntries()
  _notify()
}

export type NodeCatalogFetchResult = {
  entries: CanvasNodeCatalogEntry[]
  availability_by_node_type?: Record<string, string>
}

/** Load tenant catalog once per (userId, workspaceId); icon_ref becomes session assignment SoT. */
export async function ensureWorkspaceNodeCatalogLoaded(
  workspaceId: string,
  userId?: string | null,
  /** Optional injector for node tests — production callers omit this. */
  fetchCatalog?: (workspaceId: string) => Promise<NodeCatalogFetchResult>,
): Promise<void> {
  if (!workspaceId) return
  const key = catalogCacheKey(workspaceId, userId)
  // Only READY is cached for the key; an ERROR is retried by the next call
  // (Canvas entry / retry button) instead of staying blank for the session.
  if (_fetchedForKey === key && _status === 'ready') return
  if (_inflight && _inflight.key === key) return _inflight.promise

  // Switching identity/workspace: leave LOADING (deny creatable) until fetch settles.
  if (_fetchedForKey !== key) {
    _fetchedForKey = null
    _status = 'loading'
    _clearEntries()
    _notify()
  }

  const token = ++_requestSeq
  const promise = (async () => {
    try {
      const payload = fetchCatalog
        ? await fetchCatalog(workspaceId)
        : await (async () => {
            const { getWorkspaceNodeCatalog } = await import('../../../api')
            const res = await getWorkspaceNodeCatalog(workspaceId)
            return {
              entries: res.data.entries ?? [],
              availability_by_node_type: res.data.availability_by_node_type,
            }
          })()
      if (token !== _requestSeq) return // superseded by a newer identity/workspace
      const entries = payload.entries ?? []
      _fetchedForKey = key
      _status = 'ready'
      applyTenantNodeCatalog(entries, payload.availability_by_node_type)
      // Icon assignment is best-effort UI chrome; skip in injected test fetches
      // that never need React icon tables.
      if (!fetchCatalog) {
        await _applyCatalogIconAssignments(entries, workspaceId)
      }
    } catch {
      if (token !== _requestSeq) return
      // Fail-CLOSED for palette visibility (TASK-00583): never treat a load
      // error as "all node types creatable". Backend still enforces create.
      _fetchedForKey = key
      _status = 'error'
      _clearEntries()
      _notify()
    } finally {
      if (_inflight?.token === token) _inflight = null
    }
  })()
  _inflight = { key, promise, token }
  return promise
}

/** Retry button after an ERROR: drops the failed state and fetches again. */
export function retryWorkspaceNodeCatalog(workspaceId: string, userId?: string | null): Promise<void> {
  _fetchedForKey = null
  return ensureWorkspaceNodeCatalogLoaded(workspaceId, userId)
}

export function resetNodeCatalogCacheForTests(): void {
  invalidateNodeCatalogCache()
}
